/**
 * storageAccountLabel — ONE place that decides how a storage account is named on screen.
 *
 * 🛑 THE RULE (owner decision, 2026-08-10): **show both, identity first.**
 *    The ACCOUNT NAME is the identity; `display_name` is context beside it, never instead of it.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * Measured 2026-08-10: **20 backend files render a storage-account identity, and only 10 of them
 * used `COALESCE(display_name, account_name)`.** The same account could therefore appear as two
 * different strings depending on which screen you were on, and nothing reconciled them. That split
 * predates any recent change — it was simply invisible while no account had a `display_name`.
 *
 * It stopped being invisible the moment two did. In the destination picker,
 * `ddsfcbackupwus2` rendered as "Backup - West US 2 (lakehouse / server-side)", **sorted under B**,
 * nowhere near the `ddsfc*` block an operator scrolls to — so it read as *missing from the list*.
 * The same collapse also fed `envHint`, silently dropping the `(dev)`/`(prod)` badge that every
 * neighbouring row carries.
 *
 * An operator choosing a destination cross-references the Azure portal, the private endpoints, the
 * DNS records and the cost reports. **All of those show the account name.** A UI that shows
 * something else is asking them to hold a translation table in their head.
 *
 * ── WHY A HELPER RATHER THAN A CONVENTION ───────────────────────────────────
 *
 * A convention is what produced the 10-vs-10 split. Spec 259 hit the identical problem with cost
 * labels — seven inline `'Actual $'` strings and duplicated formatters — and fixed it with one
 * shared helper plus an oracle test. Same remedy: the decision lives here, and changing it is one
 * edit rather than twenty.
 */

export interface StorageAccountLike {
  /** The Azure account name. THE identity — what every other system shows. */
  name: string;
  /** Optional human description. Context only; never rendered alone. */
  displayName?: string | null;
}

/**
 * A single-line label: `accountname (Friendly description)`, or just `accountname` when there is
 * no description or it merely echoes the name.
 *
 * Used wherever only one string fits — a `<select>` option, a table cell, a toast. The account
 * name is FIRST so the list sorts and scans by the identity, which is the property that was lost.
 *
 * 🛑 A DESCRIPTION MUST NOT CONTAIN PARENTHESES. It is rendered inside them, so
 * "Backup - West US 2 (lakehouse / server-side)" nests into
 * `ddsfcbackupwus2 (Backup - West US 2 (lakehouse / server-side))` — unreadable, and worse in a
 * `<select>` where it cannot wrap. Migration 287 rewrote the two seeded values for exactly this.
 * The format is not defended in code: sanitising here would hide a bad value rather than fix it,
 * and the value is what other surfaces read.
 */
export function storageAccountLabel(a: StorageAccountLike): string {
  const desc = a.displayName?.trim();
  return desc && desc !== a.name ? `${a.name} (${desc})` : a.name;
}

/**
 * The two parts, for surfaces that can render a primary line and a subtitle (a card, a detail
 * header, the two-line rows the Backups tab already uses for entities).
 *
 * `subtitle` is null when there is nothing to add, so a caller can omit the element entirely
 * rather than render an empty one.
 */
export function storageAccountParts(a: StorageAccountLike): { primary: string; subtitle: string | null } {
  const desc = a.displayName?.trim();
  return { primary: a.name, subtitle: desc && desc !== a.name ? desc : null };
}
